import {Component, inject, Input, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Select} from '@core/components';
import {ModalService, RiskService} from '@core/services';
import {RiskDeclarationRow} from '@core/types';
import {Confirm} from '@core/modals';

type DeclarationType = 'boolean' | 'numeric';
type NumericComparator = 'greater_than' | 'less_than' | 'equal' | 'less_or_equal' | 'greater_or_equal' | 'not_equal';

@Component({
  selector: 'penal-add-declaration',
  imports: [Select, FormsModule],
  templateUrl: './add-declaration.html',
  styleUrl: './add-declaration.scss',
  standalone: true,
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;'
  }
})
export class AddDeclaration implements OnInit {
  declarationId: string | number | null = null;
  declarationVariableId: string | number | null = null;
  @Input() phaseOneConfirmation = false;
  @Input()
  set declaration(value: RiskDeclarationRow | null) {
    if (!value) return;
    this.declarationId = value.id;
    this.declarationVariableId = value.variableId ?? null;
    this.name = value.name || '';
    this.description = value.description || '';
    this.type = this.normalizeType(value.type);
    this.variableName = value.variableName || '';
    this.selectedConceptIds = Array.isArray(value.conceptIds) ? [...value.conceptIds] : [];
    this.declarationValue = value.threshold !== undefined && value.threshold !== null ? String(value.threshold) : '';
    this.ruleComparator = value.comparator ?? 'greater_than';
    this.ruleThreshold = '';
    this.booleanAnswer = (value.booleanAnswer === 'no' ? 'no' : 'yes');
    this.booleanRuleAnswer = this.booleanAnswer;
    if (this.declarationVariableId) {
      void this.loadVariableRuleConfig(this.declarationVariableId);
    }
    void this.loadConceptOptions();
  }

  name = '';
  description = '';
  variableName = '';
  type: DeclarationType = 'boolean';
  declarationValue = '';
  ruleComparator: NumericComparator = 'greater_than';
  ruleThreshold = '';
  ruleWeight = '';
  booleanAnswer: 'yes' | 'no' = 'yes';
  booleanRuleAnswer: 'yes' | 'no' = 'yes';
  selectedConceptIds: Array<string | number> = [];
  conceptOptions: Array<{label: string; value: string | number}> = [];

  isSubmitting = false;
  isLoadingConcepts = false;
  isLoadingVariableConfig = false;
  errorMessage = '';

  readonly typeOptions: Array<{label: string; value: DeclarationType}> = [
    {label: 'Booleana', value: 'boolean'},
    {label: 'Numérica', value: 'numeric'},
  ];
  readonly associationComparatorOptions: Array<{label: string; value: NumericComparator}> = [
    {label: 'Mayor que', value: 'greater_than'},
    {label: 'Menor que', value: 'less_than'},
    {label: 'Igual', value: 'equal'},
    {label: 'Menor o igual', value: 'less_or_equal'},
    {label: 'Mayor o igual', value: 'greater_or_equal'},
    {label: 'Distinto', value: 'not_equal'},
  ];
  readonly booleanAnswerOptions: Array<{label: string; value: 'yes' | 'no'}> = [
    {label: 'Sí', value: 'yes'},
    {label: 'No', value: 'no'},
  ];

  private readonly modal = inject(ModalService);
  private readonly riskService = inject(RiskService);

  async ngOnInit(): Promise<void> {
    await this.loadConceptOptions();
  }

  get isEditMode(): boolean {
    return !!this.declarationId;
  }

  get hasVariableName(): boolean {
    return this.variableName.trim().length > 0;
  }

  get effectiveRuleComparator(): NumericComparator {
    return this.type === 'boolean' ? 'equal' : this.ruleComparator;
  }

  get effectiveRuleThreshold(): string {
    if (this.type === 'boolean') {
      return this.booleanRuleAnswer === 'yes' ? '1' : '0';
    }

    return this.ruleThreshold.trim().replace(',', '.');
  }

  get effectiveDeclarationValue(): string {
    if (this.type === 'boolean') {
      return this.booleanAnswer === 'yes' ? '1' : '0';
    }

    return this.declarationValue.trim().replace(',', '.');
  }

  cancel(): void {
    this.modal.closeTop(false);
  }

  async submit(): Promise<void> {
    if (this.isSubmitting) return;
    this.errorMessage = '';

    if (this.phaseOneConfirmation) {
      const confirmed = await this.askForConfirmation(
        'AVISO - MODIFICACIÓN DE DECLARACIÓN',
        'La modificación de esta declaración impactará el riesgo inherente de forma inmediata. Al salir de Fase 1 el cambio será irreversible. ¿Confirmas la modificación?',
        'Modificar'
      );
      if (!confirmed) return;
    }

    const trimmedName = this.name.trim();
    if (!trimmedName) {
      this.errorMessage = 'El nombre de la declaración es obligatorio.';
      return;
    }

    if (this.type === 'boolean' && (this.booleanAnswer !== 'yes' && this.booleanAnswer !== 'no')) {
      this.errorMessage = 'Debes seleccionar el valor declarado.';
      return;
    }

    if (this.type === 'numeric' && (!this.declarationValue.trim().length || Number.isNaN(Number(this.effectiveDeclarationValue)))) {
      this.errorMessage = 'El valor declarado es obligatorio.';
      return;
    }

    const hasVariable = this.hasVariableName;
    if (hasVariable) {
      if (!this.selectedConceptIds.length) {
        this.errorMessage = 'Debes seleccionar al menos un concepto para la variable.';
        return;
      }
      if (this.type === 'numeric' && (!this.ruleThreshold.trim().length || Number.isNaN(Number(this.effectiveRuleThreshold)))) {
        this.errorMessage = 'El umbral de activación de la variable es obligatorio.';
        return;
      }
      if (!this.ruleWeight.trim().length || Number.isNaN(Number(this.ruleWeight.trim().replace(',', '.')))) {
        this.errorMessage = 'El peso de la regla de variable es obligatorio.';
        return;
      }
      if (this.type === 'numeric') {
        this.ruleThreshold = this.effectiveRuleThreshold;
      }
      this.ruleWeight = this.ruleWeight.trim().replace(',', '.');
    }

    this.isSubmitting = true;
    const payload = {
      name: trimmedName,
      description: this.description.trim(),
      type: this.type,
      variableName: this.variableName.trim(),
      comparator: this.type === 'numeric' ? this.effectiveRuleComparator : undefined,
      threshold: this.type === 'numeric' ? this.effectiveDeclarationValue : undefined,
      booleanAnswer: this.type === 'boolean' ? this.booleanAnswer : undefined,
    };

    const response = this.declarationId
      ? await this.riskService.updateDeclaration(this.declarationId, payload, {skipLoader: true})
      : await this.riskService.createDeclaration(payload, {skipLoader: true});
    if (!response.success) {
      this.isSubmitting = false;
      this.errorMessage = response.message || 'No se pudo guardar la declaración.';
      return;
    }

    const persisted = response.result || null;
    const variableId = persisted?.variableId ?? this.declarationVariableId;
    if (hasVariable && variableId) {
      const syncResponse = await this.riskService.updateVariable(variableId, {
        conceptRules: this.selectedConceptIds.map(conceptId => ({
          conceptId,
          comparator: this.effectiveRuleComparator,
          threshold: this.effectiveRuleThreshold,
          weight: this.ruleWeight,
        })),
      }, {skipLoader: true});
      if (!syncResponse.success) {
        this.isSubmitting = false;
        this.errorMessage = syncResponse.message || 'La declaración se guardó, pero falló la asociación de la variable con conceptos.';
        return;
      }
    }
    this.isSubmitting = false;

    this.modal.closeTop(true);
  }

  private async loadConceptOptions(): Promise<void> {
    if (this.isLoadingConcepts) {
      return;
    }

    this.isLoadingConcepts = true;
    const response = await this.riskService.getConcepts(1, 500, {skipLoader: true});
    this.isLoadingConcepts = false;

    if (!response.success || !response.result) {
      this.conceptOptions = [];
      this.errorMessage = this.errorMessage || 'No se pudieron cargar los conceptos de riesgo.';
      return;
    }

    this.conceptOptions = response.result.map(concept => ({
      label: concept.name,
      value: concept.id,
    }));
    if (this.errorMessage === 'No se pudieron cargar los conceptos de riesgo.') {
      this.errorMessage = '';
    }
    this.selectedConceptIds = this.selectedConceptIds.filter(id => this.conceptOptions.some(option => String(option.value) === String(id)));
  }

  private async loadVariableRuleConfig(variableId: string | number): Promise<void> {
    this.isLoadingVariableConfig = true;
    const response = await this.riskService.getVariable(variableId, {skipLoader: true});
    this.isLoadingVariableConfig = false;
    if (!response.success || !response.result) return;

    const variable = response.result;
    const firstRule = Array.isArray(variable.conceptRules) && variable.conceptRules.length ? variable.conceptRules[0] : null;
    const comparator = firstRule?.comparator ?? variable.comparator;
    const threshold = firstRule?.threshold ?? variable.threshold;
    const weight = firstRule?.weight ?? variable.weight;
    if (comparator) this.ruleComparator = comparator;
    if (threshold !== undefined && threshold !== null && String(threshold).trim() !== '') {
      this.ruleThreshold = String(threshold);
      this.booleanRuleAnswer = String(threshold) === '0' ? 'no' : 'yes';
    }
    if (weight !== undefined && weight !== null && String(weight).trim() !== '') {
      this.ruleWeight = String(weight);
    }
  }
  private normalizeType(value?: string): DeclarationType {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'boolean' || normalized === 'numeric') {
      return normalized;
    }
    return 'boolean';
  }

  private askForConfirmation(title: string, description: string, confirmText: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.modal.open<Confirm, boolean>(Confirm, {
        inputs: {
          title,
          description,
          confirmText,
          cancelText: 'Cancelar',
        },
        outputs: {
          close: () => resolve(false),
          confirm: () => resolve(true),
        },
        options: {size: 'md', closeOnBackdrop: true},
      });
    });
  }
}
